import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react';

import { FilterMatchMode } from 'primereact/api';
import { addLocale, locale } from 'primereact/api';
import { Button } from 'primereact/button';
import { Column } from 'primereact/column';
import {
  DataTable,
  DataTableFilterMetaData,
  DataTablePageEvent,
  DataTableSortMeta,
  DataTableStateEvent
} from 'primereact/datatable';
import { Dialog } from 'primereact/dialog';
import { InputText } from 'primereact/inputtext';
import { Toast } from 'primereact/toast';

import 'primeicons/primeicons.css';
import 'primeflex/primeflex.css';

import { AbsenceService } from 'components/services/AbsenceService';
import AbsenceCard from 'ui/AbsenceCard';
import DeleteConfirmationDialog from 'ui/shared/DeleteConfirmationDialog';
import TablePageHeader from 'ui/shared/TablePageHeader';

import { AbsenceTemplate } from '../constants/AbsenceTemplate';
import { isSelected } from '../helpers';
import { useDebounce } from '../hooks';
import { ruLocale } from '../lang/ruLocale';
import { Absence } from '../types/interfaces';
import pageSizeValues from '../ui/shared/paginator/pageSizeValues';
import { clearFilter } from '../utils';
import VisibilityWrapper from '../utils/wrappers/VisibilityWrapper';

export default function Absences() {
  const [absences, setAbsences] = useState<Absence[]>([]);
  const [dialogVisible, setDialogVisible] = useState<boolean>(false);
  const [deleteDialogVisible, setDeleteDialogVisible] =
    useState<boolean>(false);
  const [absenceToDelete, setAbsenceToDelete] = useState<
    string | number | null
  >(null);
  const [selectedAbsence, setSelectedAbsence] =
    useState<Absence>(AbsenceTemplate);
  const [filters, setFilters] = useState<
    Record<string, DataTableFilterMetaData>
  >({
    global: { value: null, matchMode: FilterMatchMode.CONTAINS },
    name: { value: null, matchMode: FilterMatchMode.CONTAINS },
    shortName: { value: null, matchMode: FilterMatchMode.CONTAINS }
  });

  const [loading, setLoading] = useState<boolean>(true);
  const nameFilterRef = useRef<HTMLInputElement>(null);
  const descriptionFilterRef = useRef<HTMLInputElement>(null);

  const globalFilterRef = useRef<HTMLInputElement>(null);
  const toast = useRef<Toast>(null);
  const absenceCardRef = useRef<null | {
    handleCancel: () => void;
  }>(null);

  const isSelectedIdExist = isSelected(selectedAbsence);
  const [pageSize, setPageSize] = useState(
    pageSizeValues[pageSizeValues.length - 1].value
  );
  const [pageNumber, setPageNumber] = useState(1);
  const [totalCount, setTotalCount] = useState(1);
  const [first, setFirst] = useState(0);
  const [multiSortMeta, setMultiSortMeta] = useState<
    DataTableSortMeta[] | undefined | null
  >(undefined);
  const setMultiSortMetaCallback = useCallback((e: DataTableStateEvent) => {
    setLoading(true);
    setMultiSortMeta(e.multiSortMeta);
  }, []);
  const onPageChanged = useCallback(async (event: DataTablePageEvent) => {
    if (event.rows !== undefined) {
      setFirst(0);
      setPageSize(event.rows);
    }

    if (event.page !== undefined) {
      setFirst(event.first);
      setPageNumber(event.first / event.rows + 1);
    }
  }, []);

  addLocale('ru', ruLocale.ru);
  locale('ru');

  const filtersForApi = useMemo(() => {
    const sort = multiSortMeta?.find((x) => x !== undefined);
    const baseFilters = Object.keys(filters).map((x) => {
      const f = filters[x];
      return {
        field: x,
        value: (f as DataTableFilterMetaData).value,
        operator: (f as DataTableFilterMetaData).matchMode
      };
    });

    return {
      pageNumber,
      pageSize,
      sortBy: sort?.field,
      sortDirection: sort?.order === 1 ? 'asc' : 'desc',
      filters: JSON.stringify(baseFilters)
    };
  }, [filters, multiSortMeta, pageNumber, pageSize]);

  const getSchedulesCallback = useCallback(async () => {
    try {
      const result = (await AbsenceService.getAbsences(filtersForApi)).data;
      if (result) {
        setTotalCount((!result.isSuccess ? 1 : result.value.total) ?? 1);
        setAbsences(result.value?.itemsList ?? []);
      }
      setLoading(false);
    } catch (error) {
      console.error('Error fetching absences:', error);
      toast.current?.show({
        severity: 'error',
        summary: 'Ошибка',
        detail: 'Не удалось загрузить расписания',
        life: 3000
      });
      setLoading(false);
    }
  }, [filtersForApi]);

  useEffect(() => {
    getSchedulesCallback();
  }, [getSchedulesCallback]);

  const onFilterChange = (
    filter: string,
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    const value = e.target.value;
    const _filters = JSON.parse(JSON.stringify(filters));
    _filters[filter].value = value;
    setPageNumber(1);
    setFirst(0);
    setFilters(_filters);
  };

  const onFiltersChange = useCallback((e: DataTableStateEvent) => {
    setPageNumber(1);
    setFirst(0);

    const next = e.filters as Record<string, DataTableFilterMetaData>;
    setFilters(next);
  }, []);

  const { debouncedCallback: debouncedOnFilterChange } = useDebounce(
    onFilterChange,
    300
  );

  const handleInputChange =
    (filter: string) => (e: React.ChangeEvent<HTMLInputElement>) => {
      debouncedOnFilterChange(filter, e);
    };

  const addButtonHandle = () => {
    setSelectedAbsence(AbsenceTemplate);
    setDialogVisible(true);
  };

  const handleDeleteConfirmation = () => {
    handleDelete(absenceToDelete);
    setDeleteDialogVisible(false);
  };

  const handleUpdate = async (id: number, updatedSchedule: Absence) => {
    const result = await AbsenceService.updateAbsence(id, updatedSchedule);
    getSchedulesCallback();
    return result;
  };

  const handleDelete = async (id: string | number | null) => {
    if (id) {
      await AbsenceService.deleteAbsence(parseInt(id.toString()));
      getSchedulesCallback();
    }
  };

  const handleAdd = async (absences: Absence) => {
    const result = await AbsenceService.createAbsence(absences);
    getSchedulesCallback();
    return result;
  };

  const closeDialog = () => {
    // Function to close the dialog in parent
    setDialogVisible(false);
  };

  const onHideScheduleCard = () => {
    if (absenceCardRef.current !== null) {
      absenceCardRef.current.handleCancel();
    }
  };

  const editButtonHandle = () => {
    if (toast.current && !isSelectedIdExist) {
      toast.current.show({
        severity: 'warn',
        summary: 'Ошибка',
        detail: 'Выберите вид отсутствия для редактирования',
        life: 3000
      });
    }
    if (isSelectedIdExist) setDialogVisible(true);
  }

  const deleteButtonHandle = () => {
    // If a valid process is selected, render the deletion confirmation dialog.
    if (isSelectedIdExist) {
      setAbsenceToDelete(selectedAbsence.id);
      setDeleteDialogVisible(true);
    }
    if (toast.current && !isSelectedIdExist) {
      toast.current.show({
        severity: 'warn',
        summary: 'Ошибка',
        detail: 'Выберите вид отсутствия для удаления',
        life: 3000
      });
    }
  };

  const header = (  
    <TablePageHeader
      tableName="absences"
      onGlobalSearchChange={value =>
        handleInputChange('global')({ target: { value } } as React.ChangeEvent<HTMLInputElement>)
      }
      onAdd={addButtonHandle}
      onEdit={editButtonHandle}
      onDelete={deleteButtonHandle}
    />
  )

  const tableElement = document.querySelector('.p-datatable-thead');

  function offset(el: Element) {
    var rect = el.getBoundingClientRect(),
      scrollTop = window.pageYOffset || document.documentElement.scrollTop;
    return { top: rect.top + scrollTop };
  }

  const scrollHeight = useMemo(() => {
    const paginatorElement = document.querySelector('.p-paginator');
    const paginatorHeight =
      paginatorElement instanceof HTMLElement
        ? paginatorElement.offsetHeight
        : 0;
    const tableOffset =
      tableElement instanceof HTMLElement ? offset(tableElement).top : 0;
    return `calc(100vh - ${
      tableElement instanceof HTMLElement ? tableOffset + paginatorHeight : 0
    }px)`;
  }, [tableElement]);

  return (
    <div className="absences-table wrapper wrapper--main">
      <Toast ref={toast} />

      <Dialog
        header="Карточка вида отсутствия"
        visible={dialogVisible}
        style={{ minWidth: '600px', width: '600px', minHeight: 'fit-content' }}
        maximizable
        modal
        onHide={() => onHideScheduleCard()}
      >
        <VisibilityWrapper code="absence-card__roles">
          <AbsenceCard
            scheduleProps={selectedAbsence}
            onUpdate={handleUpdate}
            onDelete={handleDelete}
            onAdd={handleAdd}
            closeDialog={closeDialog}
            ref={absenceCardRef}
          />
        </VisibilityWrapper>
      </Dialog>

      <DeleteConfirmationDialog
        visible={deleteDialogVisible}
        onHide={() => setDeleteDialogVisible(false)}
        onConfirm={handleDeleteConfirmation}
        entityType="вид отсутствия"
        entityName={selectedAbsence?.name}
      />
      <DataTable
        filters={filters}
        paginator
        first={first}
        rows={pageSize}
        onPage={onPageChanged}
        totalRecords={totalCount}
        rowsPerPageOptions={[10, 25, 50, 100]}
        paginatorLeft={' '}
        paginatorRight={`Всего: ${absences.length > 0 ? totalCount : 0}`}
        paginatorTemplate="RowsPerPageDropdown FirstPageLink PrevPageLink CurrentPageReport NextPageLink LastPageLink JumpToPageInput"
        currentPageReportTemplate="{currentPage} из {totalPages}"
        lazy
        multiSortMeta={multiSortMeta}
        onSort={setMultiSortMetaCallback}
        selectionMode="single"
        selection={selectedAbsence!}
        onSelectionChange={(e) => setSelectedAbsence(e.value as Absence)}
        onRowDoubleClick={(e) => {
          setDialogVisible(true);
          setSelectedAbsence(e.data as Absence);
        }}
        metaKeySelection={false}
        scrollable
        sortMode="multiple"
        removableSort
        scrollHeight={scrollHeight}
        size="small"
        resizableColumns
        reorderableColumns
        reorderableRows
        value={absences}
        dataKey="id"
        filterDisplay="row"
        loading={loading}
        header={header}
        emptyMessage="виды отсутствий не найдены."
        onRowReorder={(e) => setAbsences(e.value)}
        tableStyle={{ minWidth: '50rem' }}
        onFilter={onFiltersChange}
      >
        <Column
          columnKey="name"
          sortable
          field="name"
          header="Название"
          filter
          onFilterClear={() =>
            clearFilter(
              'name',
              filters,
              setFilters,
              nameFilterRef,
              setPageNumber,
              setFirst
            )
          }
          filterElement={
            <div className="flex justify-content-end">
              <InputText
                ref={nameFilterRef}
                onChange={handleInputChange('name')}
                placeholder="Поиск по названию вида отсутствия"
              />
            </div>
          }
          style={{ width: '40%' }}
        />
        <Column
          columnKey="shortName"
          sortable
          field="shortName"
          header="Короткое название"
          sortField="shortName"
          filter
          onFilterClear={() =>
            clearFilter(
              'shortName',
              filters,
              setFilters,
              descriptionFilterRef,
              setPageNumber,
              setFirst
            )
          }
          filterElement={
            <div className="flex justify-content-end">
              <InputText
                ref={descriptionFilterRef}
                onChange={handleInputChange('shortName')}
                placeholder="Поиск по короткому названию"
              />
            </div>
          }
          style={{ width: '60%' }}
        />
      </DataTable>
    </div>
  );
}
